import type { UploadedImage } from '@prezly/uploadcare';
import classNames from 'classnames';

import type { DataAttributes } from '@/types';
import { Image } from '@/ui/Image/Image';

import styles from './Avatar.module.scss';

interface Props extends DataAttributes {
    src?: UploadedImage;
    author?: string;
    size?: 'normal' | 'small' | 'tiny';
    showInitials?: boolean;
    className?: string;
}

function getInitials(name: string | undefined) {
    if (!name) {
        return '?';
    }

    return name
        .split(' ')
        .map((word) => (word && word.length ? word[0].toUpperCase() : ''))
        .join('');
}

function getSizes(size: Props['size']): Image.Props['sizes'] {
    switch (size) {
        case 'tiny':
            return `45px`;
        case 'small':
            return `65px`;
        default:
            return `500px, (min-width: 768px) 180px, (min-width: 992px) 255px`;
    }
}

export function Avatar({
    author,
    src,
    showInitials,
    size = 'normal',
    className,
    ...attributes
}: Props) {
    const initials = getInitials(author);
    const sizes = getSizes(size);

    const baseClassName = classNames(styles.container, className, {
        [styles.small]: size === 'small',
        [styles.tiny]: size === 'tiny',
    });

    if (!src) {
        if (!showInitials) {
            return null;
        }

        return (
            <div {...attributes} className={classNames(baseClassName, styles.initials)}>
                {initials}
            </div>
        );
    }

    return (
        <Image
            {...attributes}
            src={src}
            className={classNames(baseClassName, styles.image)}
            alt={author ?? ''}
            sizes={sizes}
        />
    );
}
